import type { PropsWithChildren } from "react";

import { BACKGROUND_OPTIONS, Dialog, DialogCloseButton } from "@ui/components/Dialog";
import { Typography } from "@ui/components/Typography";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import Logo from "@ui/icons/logo.svg";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";

import styles from "./ConsentReminderDialog.module.scss";

export type ConsentReminderDialogProps = {
  isOpened: boolean;
  mainImage?: string;
  desktopImage?: string;
  mobileImage?: string;
  imageAltText: string;
  title: string;
  description?: string;
  primaryButtonText: string;
  primaryButtonHref?: string;
  onPrimaryButtonClick?: () => void;
  secondaryButtonText?: string;
  secondaryButtonHref?: string;
  onSecondaryButtonClick?: () => void;
  closable?: boolean;
  onClose?: () => void;
  subtitle?: string;
  isPrimaryButtonPending?: boolean;
  isSecondaryButtonPending?: boolean;
  backgroundColor?: keyof typeof BACKGROUND_OPTIONS;
  isImageOnLeft?: boolean;
  dataTestId?: string;
};

export const ConsentReminderDialog = ({
  isOpened,
  mainImage,
  desktopImage,
  mobileImage,
  imageAltText,
  title,
  description,
  primaryButtonText,
  primaryButtonHref,
  onPrimaryButtonClick,
  secondaryButtonText,
  secondaryButtonHref,
  onSecondaryButtonClick,
  closable = false,
  onClose,
  subtitle,
  isPrimaryButtonPending = false,
  isSecondaryButtonPending = false,
  backgroundColor = "blurredWhite",
  children,
  isImageOnLeft = true,
  dataTestId = "consentReminderDialog",
}: PropsWithChildren<ConsentReminderDialogProps>) => {
  const { breakpoint } = useDeviceBreakpoint();
  const size = breakpoint === "desktop" ? "lg" : "sm";
  const [preferredImage, fallbackImage] =
    breakpoint === "mobile" ? [mobileImage, desktopImage] : [desktopImage, mobileImage];
  const responsiveImage = preferredImage || fallbackImage || mainImage;
  const hasImage = Boolean(responsiveImage);

  return (
    <Dialog
      dataTestId={dataTestId}
      isOpened={isOpened}
      closable={false}
      onClose={closable ? onClose : undefined}
      fullscreen={breakpoint === "mobile"}
      className={classNames(styles.dialog, styles.consentReminderDialog, {
        [styles.imageOnRight]: hasImage && !isImageOnLeft,
      })}
      backgroundColor={BACKGROUND_OPTIONS[backgroundColor].color}
      backgroundBlur={BACKGROUND_OPTIONS[backgroundColor].blur}
      withContentPadding={false}
    >
      <div
        className={classNames(styles.container, {
          [styles.withImage]: hasImage,
          [styles.imageOnRight]: hasImage && !isImageOnLeft,
        })}
      >
        <div className={classNames(styles.content, { [styles.closableContent]: closable })}>
          {/* TODO: A workaround to place close button inside of the content of popup to prevent images from being cropped until proper Dialog/Modal is implemented in scope of DSv3. And not to make regression in other places where Dialog is used*/}
          {closable && <DialogCloseButton onClick={onClose} containerClassName={styles.dialogCloseButtonWrapper} />}
          <div className={styles.contentBody}>
            <div className={styles.headerContent}>
              <Logo className={styles.logo} />
              <div className={styles.mainContent}>
                {subtitle && (
                  <Typography weight="light" variant="body2">
                    {subtitle}
                  </Typography>
                )}
                <h1 className={styles.title}>{title}</h1>
                {description && <p className={styles.description}>{description}</p>}
                {children}
              </div>
            </div>

            <div className={styles.buttons}>
              <Button
                {...(primaryButtonHref ? { href: primaryButtonHref } : {})}
                variant="primary"
                size={size}
                onClick={onPrimaryButtonClick}
                pending={isPrimaryButtonPending}
                data-testid="consent-reminder-primary-button"
              >
                {primaryButtonText}
              </Button>
              {secondaryButtonText && (
                <Button
                  {...(secondaryButtonHref ? { href: secondaryButtonHref } : {})}
                  variant="secondary"
                  size={size}
                  onClick={onSecondaryButtonClick}
                  pending={isSecondaryButtonPending}
                >
                  {secondaryButtonText}
                </Button>
              )}
            </div>
          </div>
        </div>

        {hasImage && (
          <div className={styles.imageContainer}>
            <ImageBehaviour src={responsiveImage} alt={imageAltText} className={styles.image} />
          </div>
        )}
      </div>
    </Dialog>
  );
};
